import React, { useEffect, useState } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
  useLocation
} from 'react-router-dom';
import './App.css';
import './mobile-overrides.css';
import Loading from './components/Loading';
import { AuthProvider } from './contexts/AuthContext';
import RequireAuth from './components/RequireAuth';

// Importar sistema de notificaciones
import { NotificationProvider } from './contexts/NotificationContext';
import NotificationCenter from './components/NotificationCenter';
import ToastNotification from './components/ToastNotification';
import { useNotificationService } from './services/NotificationService';
import { supabase } from './supabaseClient';

// Importar sistema de chat
import { RealChatProvider } from './contexts/RealChatContext';
import ChatList from './components/ChatList';
import ChatIndividual from './components/ChatIndividual';
import AuthCallback from './components/AuthCallback';

// Importar componentes críticos de arranque
import Landing from './components/Landing';

// Lazy load de rutas pesadas para mejorar TTI
const SoyIyireMapa = React.lazy(() => import('./components/SoyIyireMapa'));
const SoyIyireTrabajos = React.lazy(() => import('./components/SoyIyireTrabajos'));
const SoyIyireDetalleTrabajo = React.lazy(() => import('./components/SoyIyireDetalleTrabajo'));
const SoyIyireTipoServicio = React.lazy(() => import('./components/SoyIyireTipoServicio'));
const SoyIyireHabilidades = React.lazy(() => import('./components/SoyIyireHabilidades'));
const SoyIyireRegister = React.lazy(() => import('./components/SoyIyireRegister'));
const SoyIyireLogin = React.lazy(() => import('./components/SoyIyireLogin'));
const SoyIyireGoogleOnboarding = React.lazy(() => import('./components/SoyIyireGoogleOnboarding'));
const BuscoIyireHabilidades = React.lazy(() => import('./components/BuscoIyireHabilidades'));
const BuscoIyirePerfiles = React.lazy(() => import('./components/BuscoIyirePerfiles'));
const BuscoIyireDetallePerfil = React.lazy(() => import('./components/BuscoIyireDetallePerfil'));
const BuscoIyireCertificado = React.lazy(() => import('./components/BuscoIyireCertificado'));
const BuscoIyireRegister = React.lazy(() => import('./components/BuscoIyireRegister'));
const BuscoIyireLogin = React.lazy(() => import('./components/BuscoIyireLogin'));
const BuscoIyireGoogleOnboarding = React.lazy(() => import('./components/BuscoIyireGoogleOnboarding'));
const ConfirmarEmail = React.lazy(() => import('./components/ConfirmarEmail'));
const AdminPanel = React.lazy(() => import('./components/AdminPanel'));
const AdminLogin = React.lazy(() => import('./components/AdminLogin'));
const ResetPassword = React.lazy(() => import('./components/ResetPassword'));
const ProfilePage = React.lazy(() => import('./components/ProfilePage'));
const TerminosCondiciones = React.lazy(() => import('./components/TerminosCondiciones'));
const PoliticaPrivacidad = React.lazy(() => import('./components/PoliticaPrivacidad'));
const HelpPage = React.lazy(() => import('./components/HelpPage'));
const Seleccion = React.lazy(() => import('./Seleccion'));
const PagoExitoso = React.lazy(() => import('./components/PagoExitoso'));
const PagoPendiente = React.lazy(() => import('./components/PagoPendiente'));
const PagoError = React.lazy(() => import('./components/PagoError'));
const AcceptInvitation = React.lazy(() => import('./components/AcceptInvitation'));

// Wrapper components para manejar la navegación correctamente
function BuscoIyireRegisterWrapper() {
  const navigate = useNavigate();
  return <BuscoIyireRegister navigate={navigate} />;
}

function BuscoIyireLoginWrapper() {
  const navigate = useNavigate();
  return <BuscoIyireLogin navigate={navigate} />;
}

function BuscoIyireConfirmarEmailWrapper() {
  const navigate = useNavigate();
  return <ConfirmarEmail navigate={navigate} userType="busco-niva" />;
}

function BuscoIyireHabilidadesWrapper() {
  const navigate = useNavigate();
  return <BuscoIyireHabilidades navigate={navigate} />;
}

function BuscoIyirePerfilesWrapper() {
  const navigate = useNavigate();
  return <BuscoIyirePerfiles navigate={navigate} />;
}

function BuscoIyireDetallePerfilWrapper() {
  const navigate = useNavigate();
  return <BuscoIyireDetallePerfil navigate={navigate} />;
}

function BuscoIyireCertificadoWrapper() {
  const navigate = useNavigate();
  return <BuscoIyireCertificado navigate={navigate} />;
}

function SoyIyireLoginWrapper() {
  const navigate = useNavigate();
  return <SoyIyireLogin navigate={navigate} />;
}

function SoyIyireRegisterWrapper() {
  const navigate = useNavigate();
  return <SoyIyireRegister navigate={navigate} />;
}

function SoyIyireConfirmarEmailWrapper() {
  const navigate = useNavigate();
  return <ConfirmarEmail navigate={navigate} userType="soy-niva" />;
}

function SoyIyireHabilidadesWrapper() {
  const navigate = useNavigate();
  return <SoyIyireHabilidades navigate={navigate} />;
}

function SoyIyireTipoServicioWrapper() {
  const navigate = useNavigate();
  return <SoyIyireTipoServicio navigate={navigate} />;
}

function SoyIyireMapaWrapper() {
  const navigate = useNavigate();
  return <SoyIyireMapa navigate={navigate} />;
}

function SoyIyireTrabajosWrapper() {
  const navigate = useNavigate();
  return <SoyIyireTrabajos navigate={navigate} />;
}

function SoyIyireDetalleTrabajoWrapper() {
  const navigate = useNavigate();
  return <SoyIyireDetalleTrabajo navigate={navigate} />;
}

function ProfilePageWrapper() {
  const navigate = useNavigate();
  return <ProfilePage navigate={navigate} />;
}

function ResetPasswordWrapper() {
  const navigate = useNavigate();
  return <ResetPassword navigate={navigate} />;
}

// (Welcome eliminado por no uso)

// Selección ahora vive en src/Seleccion.tsx

// Componente para manejar las notificaciones de demostración
function NotificationDemo() {
  const notificationService = useNotificationService();

  useEffect(() => {
    // Solicitar permisos de notificación al cargar la app
    notificationService.requestNotificationPermission();
    
    // COMENTADO: Notificaciones de demostración deshabilitadas
    // const timer = setTimeout(() => {
    //   notificationService.startDemoNotifications();
    // }, 3000);

    // return () => clearTimeout(timer);
  }, [notificationService]);

  return null;
}

// Reemplazado por componente Loading en components/Loading.tsx

function AppShell({ adminAuth, handleAdminLogin, handleAdminLogout, hasSession }: { adminAuth: boolean; handleAdminLogin: () => void; handleAdminLogout: () => void; hasSession: boolean; }) {
  const location = useLocation();
  const isLanding = location.pathname === '/';
  return (
    <>
      {!isLanding && (
        <>
          <NotificationDemo />
          <ToastNotification />
          <NotificationCenter />
          {hasSession && (
            <>
              <ChatList />
              <ChatIndividual />
            </>
          )}
        </>
      )}
      <React.Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/seleccion" element={<Seleccion />} />
          
          {/* Rutas SOY NIVA */}
          <Route path="/soy-niva/login" element={<SoyIyireLoginWrapper />} />
          <Route path="/soy-niva/registro" element={<SoyIyireRegisterWrapper />} />
          <Route path="/soy-niva/registro-google" element={
            <RequireAuth>
              <SoyIyireGoogleOnboarding />
            </RequireAuth>
          } />
          <Route path="/soy-niva/confirmar-email" element={<SoyIyireConfirmarEmailWrapper />} />
          <Route path="/soy-niva/habilidades" element={
            <RequireAuth>
              <SoyIyireHabilidadesWrapper />
            </RequireAuth>
          } />
          <Route path="/soy-niva/tipo-servicio" element={
            <RequireAuth>
              <SoyIyireTipoServicioWrapper />
            </RequireAuth>
          } />
          <Route path="/soy-niva/mapa" element={
            <RequireAuth>
              <SoyIyireMapaWrapper />
            </RequireAuth>
          } />
          <Route path="/soy-niva/trabajos" element={
            <RequireAuth>
              <SoyIyireTrabajosWrapper />
            </RequireAuth>
          } />
          <Route path="/soy-niva/trabajo/:id" element={
            <RequireAuth>
              <SoyIyireDetalleTrabajoWrapper />
            </RequireAuth>
          } />
          <Route path="/soy-niva/perfil" element={
            <RequireAuth>
              <ProfilePageWrapper />
            </RequireAuth>
          } />
          
          {/* Rutas BUSCO NIVA */}
          <Route path="/busco-niva/login" element={<BuscoIyireLoginWrapper />} />
          <Route path="/busco-niva/registro" element={<BuscoIyireRegisterWrapper />} />
          <Route path="/busco-niva/registro-google" element={
            <RequireAuth>
              <BuscoIyireGoogleOnboarding />
            </RequireAuth>
          } />
          <Route path="/busco-niva/confirmar-email" element={<BuscoIyireConfirmarEmailWrapper />} />
          <Route path="/busco-niva/habilidades" element={
            <RequireAuth>
              <BuscoIyireHabilidadesWrapper />
            </RequireAuth>
          } />
          <Route path="/busco-niva/perfiles" element={
            <RequireAuth>
              <BuscoIyirePerfilesWrapper />
            </RequireAuth>
          } />
          <Route path="/busco-niva/perfil/:id" element={
            <RequireAuth>
              <BuscoIyireDetallePerfilWrapper />
            </RequireAuth>
          } />
          <Route path="/busco-niva/perfil" element={
            <RequireAuth>
              <ProfilePageWrapper />
            </RequireAuth>
          } />
          <Route path="/busco-niva/certificado" element={
            <RequireAuth>
              <BuscoIyireCertificadoWrapper />
            </RequireAuth>
          } />
          <Route path="/reset-password" element={<ResetPasswordWrapper />} />
          <Route path="/admin" element={adminAuth ? <AdminPanel onLogout={handleAdminLogout} /> : <AdminLogin onLogin={handleAdminLogin} />} />
          <Route path="/terminos-condiciones" element={<TerminosCondiciones />} />
          <Route path="/privacidad" element={<PoliticaPrivacidad />} />
          <Route path="/ayuda" element={<HelpPage />} />
          {/* Rutas de retorno de pago */}
          <Route path="/pago-exitoso" element={<PagoExitoso />} />
          <Route path="/pago-pendiente" element={<PagoPendiente />} />
          <Route path="/pago-error" element={<PagoError />} />
          <Route path="/auth/accept-invitation" element={<AcceptInvitation />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
        </Routes>
      </React.Suspense>
    </>
  );
}

function App() {
  const [showLoader, setShowLoader] = useState(true);
  const [adminAuth, setAdminAuth] = React.useState(false);
  const [hasSession, setHasSession] = useState(false);
  const handleAdminLogin = () => setAdminAuth(true);
  const handleAdminLogout = () => { setAdminAuth(false); };
  useEffect(() => {
    const t = setTimeout(() => setShowLoader(false), 1000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (mounted) setHasSession(!!session);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (mounted) setHasSession(!!session);
    });
    return () => { mounted = false; subscription.unsubscribe(); };
  }, []);
  if (showLoader) return <Loading />;
  return (
    <NotificationProvider>
      <AuthProvider>
        <Router>
          {hasSession ? (
            <RealChatProvider>
              <AppShell adminAuth={adminAuth} handleAdminLogin={handleAdminLogin} handleAdminLogout={handleAdminLogout} hasSession={true} />
            </RealChatProvider>
          ) : (
              <AppShell adminAuth={adminAuth} handleAdminLogin={handleAdminLogin} handleAdminLogout={handleAdminLogout} hasSession={false} />
          )}
        </Router>
      </AuthProvider>
    </NotificationProvider>
    );
  }

export default App;
